import React, { useState } from 'react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { SearchInput } from '../ui/SearchInput';
import { GlobalProjectSelector } from './GlobalProjectSelector';
import { 
  Bell, 
  Settings, 
  LogOut,
  User,
  ChevronDown,
  HelpCircle,
  Moon,
  Sun,
  Menu,
  Search
} from 'lucide-react';

interface HeaderProps {
  onMobileSidebarToggle?: () => void;
}

export function Header({ onMobileSidebarToggle }: HeaderProps) {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  const handleSearch = (query: string) => {
    console.log('Search:', query);
    // Implement global search functionality
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
  };

  const userName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'User';
  const userInitial = userName.charAt(0).toUpperCase();

  return (
    <>
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-16 sticky top-0 z-40 transition-colors">
        <div className="flex items-center justify-between h-full px-4 sm:px-6">
          {/* Left side - Mobile menu button + Search */}
          <div className="flex items-center flex-1">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onMobileSidebarToggle}
              className="lg:hidden mr-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Desktop Search */}
            <div className="hidden sm:block flex-1 max-w-2xl">
              <SearchInput
                placeholder="Search projects, reports, or ask AI..."
                onSearch={handleSearch}
              />
            </div>

            {/* Mobile Search Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMobileSearch(!showMobileSearch)}
              className="sm:hidden text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              <Search className="w-5 h-5" />
            </Button>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Global Project Selector */}
            <div className="hidden sm:block">
              <GlobalProjectSelector />
            </div>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {/* Help - Hidden on mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="hidden sm:block text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>

            {/* Notifications - Hidden on mobile */}
            <div className="relative hidden sm:block">
              <Button variant="ghost" size="icon" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                <Bell className="w-5 h-5" />
              </Button>
              {/* Notification badge */}
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
            </div>

            {/* User Menu */}
            <div className="relative">
              <Button 
                variant="ghost" 
                className="flex items-center space-x-2 px-2 sm:px-3"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {userInitial}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{userName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Member</p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 hidden sm:block" />
              </Button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <p className="font-medium text-gray-900 dark:text-white">{userName}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{user?.user_metadata?.company_name || 'No company'}</p>
                    
                    {/* Mobile Project Selector */}
                    <div className="sm:hidden mt-3">
                      <GlobalProjectSelector />
                    </div>
                  </div>
                  
                  <div className="p-2">
                    <a
                      href="/settings/profile"
                      className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                    >
                      <User className="w-4 h-4 mr-3" />
                      Profile Settings
                    </a>
                    <a
                      href="/settings"
                      className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                    >
                      <Settings className="w-4 h-4 mr-3" />
                      Account Settings
                    </a>
                    <a
                      href="/billing"
                      className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                    >
                      <Bell className="w-4 h-4 mr-3" />
                      Billing & Usage
                    </a>
                    {/* Mobile-only links */}
                    <div className="sm:hidden border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                      <a
                        href="/help"
                        className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                      >
                        <HelpCircle className="w-4 h-4 mr-3" />
                        Help & Support
                      </a>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                    <button
                      onClick={handleSignOut}
                      className="flex items-center w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Search Overlay */}
      {showMobileSearch && (
        <div className="sm:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
          <SearchInput
            placeholder="Search projects, reports, or ask AI..."
            onSearch={(query) => {
              handleSearch(query);
              setShowMobileSearch(false);
            }}
          />
        </div>
      )}
    </>
  );
}